import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from 'src/app/services/shared.service';
import { AppConstants } from 'src/app/types/constants/app.constant';
import { LTA_TYPES } from 'src/app/types/enums/list-to-assign.enum';
import { MediaGroup } from 'src/app/types/enums/media.enum';
import { InspectionMediaStatus } from 'src/app/types/enums/other.enum';
import { IFileUpload, IMediaInfo, IMediaTicket } from 'src/app/types/interfaces/common.interface';
import { invalidField, translateWithGroupPrefix } from '../../utils/helper';
import { ValidationHelper } from '../../utils/validation-helper';
import { CommonModalComponent } from '../common-modal/common-modal.component';
import { CommonModalService } from '../common-modal/common-modal.service';
import { ToastService } from '../toast/toast.service';

@Component({
  selector: 'app-modal-upload-media',
  templateUrl: './modal-upload-media.component.html',
  styleUrls: ['./modal-upload-media.component.scss']
})
export class ModalUploadMediaComponent {
  @ViewChild('modal') private modalComponent: CommonModalComponent;
  form: FormGroup;
  @Input() media: IMediaInfo;
  invalidField = invalidField;
  getError = ValidationHelper.getErrorValidation;
  typeSelection = Object.values(InspectionMediaStatus);
  mediaEmptyError: string = '';
  originalMedia: IMediaInfo;
  @Output() successUpload:EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private commonModalService: CommonModalService,
    private cdr: ChangeDetectorRef
  ) {}

  async openModal(media: IMediaInfo) {
    this.originalMedia = media;
    this.init();
    this.cdr.detectChanges();
    return await this.modalComponent.open();
  }

  cloneMedia() {
    const clone = { ...this.originalMedia };
    if (this.originalMedia.file_upload_id) clone.file_upload_id = { ...this.originalMedia.file_upload_id as IFileUpload};
    if (this.originalMedia.media_tickets) clone.media_tickets = { ...this.originalMedia.media_tickets as IMediaTicket };
    return clone;
  }

  init(): void {
    this.mediaEmptyError = '';
    this.media = this.cloneMedia();

    this.form = this.fb.group({
      title: [ translateWithGroupPrefix(this.translateService, this.media?.title, 'MEDIA') ?? '', Validators.required],
      ticket: [this.media?.media_tickets ? true : false],
      category: [this.media?.media_tickets?.ticket_category ?? ''],
      comment: [this.media?.media_tickets?.comment ?? ''],
    });
    this.form.controls.title.valueChanges.subscribe(change => {
      this.media.title = change;
    })
    this.updateFormValidators();
  }

  cancel() {
    this.modalComponent.close(null);
  }

  previewClicked() {
    this.commonModalService.openImageViewerModal([this.media], 0);
  }

  updateFormValidators() {
    const validators = this.form.value.ticket? [Validators.required] : [];
    ['category', 'comment'].forEach(fname => {
      this.form.get(fname)?.setValidators(validators);
    })
  }

  isTitleFixed() {
    return [MediaGroup.MANDATORY, MediaGroup.OPTIONAL].includes( this.media.media_group as MediaGroup );
  }

  isVideo(): boolean {
    return this.media?.media_group === 'VIDEO';
  }

  createFormDataForUpload() {
    const fd = new FormData();
    fd.append('file', this.media.file!);
    fd.append('group', LTA_TYPES.INSPECTION);
    return fd;
  }
  getPromptTextForUploadBox() {
    return `COMMON.CLICK_TO_CHOOSE_${this.isVideo() ? 'VIDEO' : 'IMAGE' }`;
  }

  onSave() {
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.media.file_upload_id) {
      if (!this.media.file_upload_id) {
        this.mediaEmptyError = AppConstants.ERROR_MEDIA_EMPTY;
      }
      return false;
    }

    this.sharedService.uploadFile(this.createFormDataForUpload()).subscribe({
      next: (res) => {
        this.originalMedia.file_upload_id = res;
        if (!this.isTitleFixed()) this.originalMedia.title = this.form.value.title;
        if (this.form.value.ticket) {
          this.originalMedia.media_tickets ??= {} as IMediaTicket;
          this.originalMedia.media_tickets.ticket_category = this.form.value.category;
          this.originalMedia.media_tickets.comment = this.form.value.comment;
        }

        this.modalComponent.close(null);
        this.successUpload.emit(this.originalMedia);
      },
      error: (err) => {
        this.handleFileUploadError(err);
      },
    });

  }

  handleFileUploadError(err: any): void {
    if (err.status === 500) {
      this.toastService.error('UPLOAD_FILE.SYSTEM_ERROR');
    } else {
      this.toastService.error('An error occurred during file upload.');
    }
  }
}
